import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { useDeleteContact } from '@/hooks/useContacts'

interface DeleteContactDialogProps {
  /** Whether dialog is open */
  open: boolean
  /** Callback when dialog open state changes */
  onOpenChange: (open: boolean) => void
  /** ID of contact to delete */
  contactId: string
  /** ID of client the contact belongs to */
  clientId: string
  /** Name of contact (for display in confirmation message) */
  contactName: string
}

/**
 * Confirmation dialog for deleting a contact
 * Performs soft delete (sets deleted_at timestamp)
 */
export function DeleteContactDialog({
  open,
  onOpenChange,
  contactId,
  clientId,
  contactName,
}: DeleteContactDialogProps) {
  const deleteMutation = useDeleteContact()

  const handleDelete = async () => {
    try {
      await deleteMutation.mutateAsync({ id: contactId, clientId })
      onOpenChange(false)
    } catch {
      // Error handling is done in the mutation hook
    }
  }

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete Contact</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to delete <strong>"{contactName}"</strong>?
            <br />
            <br />
            This action cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleDelete}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? 'Deleting...' : 'Delete Contact'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
